import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {masterDataKeys} from '@/matic/features/master-data/query-keys';
import {erpProvenanceKeys} from '@/matic/features/erp/record-provenance';
import {getErpRecordFieldWriteApi} from '../api/erp-record-field-write-api';
import type {
  ErpExpectedFieldChange,
  ErpFieldChange,
  ErpFieldWritePreview,
  ErpFieldWriteResult,
} from '../types';

/**
 * Preview and commit an N-field write-through to the ERP.
 *
 * Deliberately **no toast on error**. The two interesting failures here
 * are 409s that the dialog handles in place — a changed value re-renders
 * the diff against what the ERP actually holds, and an in-flight write
 * asks the user to wait. A toast would close the loop in the wrong place:
 * the whole point of compare-and-swap is to put the user back in front of
 * the decision with correct data, not to tell them it failed. The dialog
 * owns error presentation; these hooks just surface the error.
 */
export function useErpFieldWritePreview() {
  const api = getErpRecordFieldWriteApi();

  return useMutation<
    ErpFieldWritePreview[],
    Error,
    {objectRecordId: string; changes: ErpFieldChange[]}
  >({
    async mutationFn(input) {
      return api.preview(input);
    },
  });
}

export function useErpFieldWriteCommit(typeId?: string) {
  const api = getErpRecordFieldWriteApi();
  const queryClient = useQueryClient();
  const tenantId = useRequiredTenantId();

  return useMutation<
    ErpFieldWriteResult,
    Error,
    {
      objectRecordId: string;
      changes: ErpExpectedFieldChange[];
      /**
       * One per confirm *attempt*, not per dialog-open. A re-confirm after
       * a 409 is a genuinely new decision, so it needs a new key — reusing
       * the old one would be replayed as "already done".
       */
      idempotencyKey: string;
    }
  >({
    async mutationFn(input) {
      return api.commit(input);
    },
    onSettled(_data, _error, variables) {
      // Invalidate on settled rather than on success: a write that fails
      // verification, or one whose local landing was rejected, may still
      // have moved the ERP — so the record and its provenance are both
      // suspect either way and should be refetched.
      void queryClient.invalidateQueries({
        queryKey: erpProvenanceKeys.record(variables.objectRecordId, tenantId),
      });
      // The Library/Relay surfaces render their record buckets from the
      // batch-slice query, a distinct key from objectRecordLists — so a write
      // made from a Relay tag modal left the visible value stale until a full
      // page reload. Not gated on typeId: the batch key spans types, and a
      // confirmed ERP write has changed the record whether or not this caller
      // happened to know its type.
      void queryClient.invalidateQueries({
        queryKey: masterDataKeys.objectRecordsBatchAll(),
      });
      if (typeId) {
        void queryClient.invalidateQueries({
          queryKey: masterDataKeys.objectRecord(
            typeId,
            variables.objectRecordId,
            tenantId
          ),
        });
        void queryClient.invalidateQueries({
          queryKey: masterDataKeys.objectRecordLists(typeId),
        });
      }
    },
  });
}
